import { z } from 'zod';

// Sprint status enum
export enum SprintStatusEnum {
  PLANNED = 'PLANNED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Sprint schema
export const SprintSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  name: z.string(),
  goal: z.string().nullable().optional(),
  startDate: z.date(),
  endDate: z.date(),
  status: z.nativeEnum(SprintStatusEnum).default(SprintStatusEnum.PLANNED),
  order: z.string().default("a1"),
  velocity: z.number().nullable().optional(),
  actualVelocity: z.number().nullable().optional(),
  retrospective: z.any().nullable().optional(),
  settings: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Sprint = z.infer<typeof SprintSchema>;

// Create sprint schema
export const CreateSprintSchema = SprintSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  actualVelocity: true,
  retrospective: true,
  settings: true,
}).extend({
  settings: z.any().optional(),
});

export type CreateSprint = z.infer<typeof CreateSprintSchema>;

// Update sprint schema
export const UpdateSprintSchema = SprintSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export type UpdateSprint = z.infer<typeof UpdateSprintSchema>;

// Sprint with tasks schema
export const SprintWithTasksSchema = SprintSchema.extend({
  tasks: z.array(z.any()).optional(),
  taskCount: z.number().optional(),
  completedTaskCount: z.number().optional(),
  totalStoryPoints: z.number().optional(),
  completedStoryPoints: z.number().optional(),
});

export type SprintWithTasks = z.infer<typeof SprintWithTasksSchema>;

// Sprint response schema
export const SprintResponseSchema = z.object({
  data: SprintSchema,
  message: z.string().optional(),
});

export type SprintResponse = z.infer<typeof SprintResponseSchema>;

// Sprints response schema
export const SprintsResponseSchema = z.object({
  data: z.array(SprintSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type SprintsResponse = z.infer<typeof SprintsResponseSchema>;

// Sprint metrics schema
export const SprintMetricsSchema = z.object({
  sprintId: z.string().uuid(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  inProgressTasks: z.number(),
  notStartedTasks: z.number(),
  totalStoryPoints: z.number(),
  completedStoryPoints: z.number(),
  velocity: z.number(),
  burndownData: z.array(z.object({
    date: z.date(),
    remainingPoints: z.number(),
    idealPoints: z.number(),
  })).optional(),
});

export type SprintMetrics = z.infer<typeof SprintMetricsSchema>;

// Type guards
export function isValidSprint(sprint: unknown): sprint is Sprint {
  if (!sprint || typeof sprint !== 'object') return false;
  
  const sprintObj = sprint as Partial<Sprint>;
  
  return (
    typeof sprintObj.id === 'string' &&
    typeof sprintObj.organizationId === 'string' &&
    typeof sprintObj.name === 'string' &&
    sprintObj.startDate instanceof Date &&
    sprintObj.endDate instanceof Date &&
    Object.values(SprintStatusEnum).includes(sprintObj.status as SprintStatusEnum)
  );
} 